import { createSelector } from 'reselect'
import { namespace } from '.'
import { OrderType } from '../order/types'
import {
  getEmptyGridView,
  RfqDirectionState,
  Security7ChordData,
  SecurityOrderData,
  SecurityStaticData,
  State
} from './reducer'
import { identity } from 'rxjs'
import { getTypedArray } from './helpers'
import { MiniSecurityFilter } from './types'

type T = typeof namespace

export type HasNsKey = {
  [key in T]: State
}
export const getState = <H extends HasNsKey>(state: H) => state[namespace]

const getSecuritiesOrderDataInfoCache = createSelector(
  [getState],
  (state) => state.securitiesOrderDataInfoCache
)

const getSecurities7ChordDataInfoCache = createSelector(
  [getState],
  (state) => state.securities7ChordDataInfoCache
)

export const getSecuritiesStaticDataInfoCache = createSelector(
  [getState],
  (state) => state.securitiesStaticDataInfoCache
)

export const getSecurityOrderDataById = createSelector(
  [getSecuritiesOrderDataInfoCache],
  (securitiesInfoCache) => (securityId: number) =>
    securitiesInfoCache[securityId] as SecurityOrderData | undefined
)

export const getSecurityStaticDataById = createSelector(
  [getSecuritiesStaticDataInfoCache],
  (securitiesInfoCache) => (securityId: number) =>
    securitiesInfoCache[securityId] as SecurityStaticData | undefined
)

export const getSecurity7ChordDataById = createSelector(
  [getSecurities7ChordDataInfoCache],
  (securitiesInfoCache) => (securityId: number) =>
    securitiesInfoCache[securityId] as Security7ChordData | undefined
)

export const getGridCount = createSelector(
  [getState],
  (state) => Object.keys(state.gridDefinitions).length
)

export const getGridDefinition = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]
)

export const getWatchlistId = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.watchlistId
)

export const getIssuerFilter = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.issuerFilter
)

export const getQueryFilter = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.queryFilter
)

export const getAdvancedFilter = createSelector(
  [getState],
  (state) => (gridIndex: number) => {
    return state.gridDefinitions[gridIndex]?.filter
  }
)

export const getSecuritiesFilter = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.securitiesFilter
)

export const getIsMine = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]?.isMine
)

export const getShowLive = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]?.showLive
)

export const getShowRfqEligible = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.showRfqEligible ?? false
)

const defaultRfqState = { isVisible: false } as RfqDirectionState
export const getRfqDisplayStateForSecurity = createSelector(
  [getState],
  (state) => (gridIndex: number, securityId: number, direction: OrderType) => {
    const securityState =
      state.gridViewState[gridIndex]?.rfqState?.[securityId]?.[direction]
    return securityState ?? defaultRfqState
  }
)

export const getMyFirm = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]?.myFirm
)

export const getMyBook = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]?.myBook
)

export const getUseAdvancedFilter = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.useAdvancedFilter
)

export const getSize = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.gridDefinitions[gridIndex]?.size
)

export const getQuoteReliability = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.quoteReliability
)

const defaultMiniFilter: MiniSecurityFilter = {}

export const getSecurityMiniFilter = createSelector(
  [getState],
  (state) =>
    (gridIndex: number): MiniSecurityFilter => {
      const definition = state.gridDefinitions[gridIndex]
      if (!definition) return defaultMiniFilter
      const qr = definition.quoteReliability
      const filterSize = definition.size
      return { qr, filterSize }
    }
)

export const getSelectedSecurityId = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridDefinitions[gridIndex]?.selectedSecurityId
)

const getGridDefinitions = createSelector(
  [getState],
  (state) => state.gridDefinitions
)

export const getGridIndices = createSelector(
  [getGridDefinitions],
  (gridDefinitions) =>
    Object.keys(gridDefinitions)
      .map((gridIndex) => Number(gridIndex))
      .sort((i1, i2) => gridDefinitions[i1].order - gridDefinitions[i2].order)
)

export const getGridIndicesWithWatchlist = createSelector(
  [getState],
  (state) => (watchlistId: number) =>
    Object.keys(state.gridDefinitions)
      .map((gridIndex) => Number(gridIndex))
      .filter(
        (gridIndex) =>
          state.gridDefinitions[gridIndex].watchlistId === watchlistId
      )
)

export const getListradingGridData = createSelector(
  getState,
  (state) => state.listTradingSecurities
)

export const getListTradingLoadStatus = createSelector(
  [getListradingGridData],
  (gridData) => (gridData.pending ? 'pending' : gridData.error ? 'error' : '')
)

export const getSecurityByCusipOrIsin = createSelector(
  [getSecuritiesStaticDataInfoCache],
  (cache) => (symbol: string) => {
    const securities = Object.values(cache)
    return securities.find((s) => s.cusip === symbol || s.isin === symbol)
  }
)

export const getSecurityExportData = createSelector(
  [getState],
  (state) => state.securityExportData
)

export const getAllSecuritiesLoadState = createSelector(
  [getState],
  (state) => state.allSecuritiesLoadState
)

export const getAllSecuritiesLoaded = createSelector(
  [getAllSecuritiesLoadState],
  (loadState) => loadState.loaded
)

const emptyStaticDataArray = getTypedArray<SecurityStaticData>()
export const getAllCachedSecurities = createSelector(
  [getAllSecuritiesLoaded, getSecuritiesStaticDataInfoCache],
  (loaded, cache) => (loaded ? Object.values(cache) : emptyStaticDataArray)
)

const emptyIdentifierMap = {} as Record<string, number>
export const getSecurityIdentifierMap = createSelector(
  [getSecuritiesStaticDataInfoCache],
  (cache): Record<string, number> => {
    const values = Object.values(cache)
    if (values.length === 0) return emptyIdentifierMap
    const map: Record<string, number> = {}
    for (const security of values) {
      if (security.cusip) {
        map[security.cusip.toUpperCase()] = security.id
      }
      if (security.isin) {
        map[security.isin.toUpperCase()] = security.id
      }
      if (security.issuerSymbol) {
        map[security.issuerSymbol.toUpperCase()] = security.id
      }
    }
    return map
  }
)

export const findSecurityBySymbol = createSelector(
  [getSecurityIdentifierMap, getSecuritiesStaticDataInfoCache],
  (identifierMap, cache) =>
    (symbol: string): SecurityStaticData | undefined => {
      if (!symbol) return undefined
      const id = identifierMap[symbol.toUpperCase()]
      return id !== undefined ? cache[id] : undefined
    }
)

export const isValidSecurity = createSelector(
  [getSecurityIdentifierMap],
  (identifierMap) =>
    (symbol: string): boolean => {
      if (!symbol) return false
      return identifierMap[symbol.toUpperCase()] !== undefined
    }
)

const emptySuggestions = getTypedArray<string>()
export const getSearchSuggestions = createSelector(
  [getAllCachedSecurities],
  (securities) => {
    if (securities.length === 0) return emptySuggestions
    return securities
      .flatMap((s) => [s.cusip, s.isin])
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
  }
)

const emptyGridView = getEmptyGridView()

export const getGridViewState = createSelector(
  [getState],
  (state) => (gridIndex: number) =>
    state.gridViewState[gridIndex] ?? emptyGridView
)

export const getSecurityStaticDataForIds = createSelector(
  [getSecuritiesStaticDataInfoCache],
  (staticData) => (securityIds: number[]) =>
    securityIds.map((id) => staticData[id]).filter(identity)
)

const emptyArr = getTypedArray<number>()
export const getSecurityStaticDataForGridView = createSelector(
  [getGridViewState, getSecurityStaticDataForIds],
  (getViewState, getStaticData) => (gridIndex: number) => {
    const state = getViewState(gridIndex)
    return getStaticData(state.securityIds ?? emptyArr)
  }
)

export const getCallStateForGridView = createSelector(
  [getGridViewState],
  (getViewState) => (gridIndex: number) => getViewState(gridIndex).callState
)
